import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Btn, EmptyState } from "@/components/primitives";
import { PdfModal } from "@/components/PdfModal";
import { toast } from "sonner";
import { CalendarDays, Plus, Eye, Loader2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const inputCls = "w-full h-10 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green transition";

export default function Schedules() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState(null);
  const [viewPath, setViewPath] = useState(null);
  const [viewTitle, setViewTitle] = useState("");
  const [open, setOpen] = useState(false);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [pdf, setPdf] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/schedules").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Please choose a PDF file"); return; }
    const reader = new FileReader();
    reader.onload = () => setPdf(reader.result); // data:application/pdf;base64,...
    reader.readAsDataURL(f);
  };

  const upload = async () => {
    if (!pdf) { toast.error("Choose a PDF to upload"); return; }
    setSaving(true);
    try {
      await api.post("/schedules", { year: Number(year), month: Number(month), pdf_b64: pdf });
      toast.success("Schedule uploaded");
      setOpen(false); setPdf(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const openView = (s) => { setViewTitle(s.title || s.label || "Schedule"); setViewPath(`/schedules/${s.id}/pdf`); };

  if (items === null) return <Loading />;

  return (
    <div>
      <PageHeader title="Monthly Schedules" subtitle="Driver rotation & on-call schedules.">
        {isAdmin && <Btn onClick={() => setOpen(true)} data-testid="upload-schedule-btn"><Plus className="w-4 h-4" /> Upload</Btn>}
      </PageHeader>

      {items.length === 0 ? (
        <Card><EmptyState icon={CalendarDays} title="No schedules yet" subtitle={isAdmin ? "Upload the first monthly schedule PDF." : "Your admin hasn't posted a schedule yet."} /></Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((s) => (
            <Card key={s.id} className="p-4 flex items-center gap-3" data-testid={`schedule-${s.id}`}>
              <span className="w-10 h-10 rounded-md bg-curtron-green/10 text-curtron-green flex items-center justify-center shrink-0"><CalendarDays className="w-5 h-5" /></span>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-curtron-ink text-sm truncate">{s.title || s.label}</div>
                <div className="text-xs text-curtron-muted truncate">Posted by {s.uploaded_by_name || "—"}</div>
              </div>
              <Btn variant="secondary" className="h-8" onClick={() => openView(s)} data-testid={`schedule-view-${s.id}`}><Eye className="w-4 h-4" /> View</Btn>
            </Card>
          ))}
        </div>
      )}

      <PdfModal path={viewPath} title={viewTitle} onClose={() => setViewPath(null)} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Monthly Schedule</DialogTitle>
            <DialogDescription>Post a schedule PDF for the team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wide font-medium text-curtron-sub">Month</label>
                <select value={month} onChange={(e) => setMonth(e.target.value)} className={`${inputCls} mt-1`} data-testid="schedule-month">
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide font-medium text-curtron-sub">Year</label>
                <input type="number" value={year} onChange={(e) => setYear(e.target.value)} className={`${inputCls} mt-1`} data-testid="schedule-year" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide font-medium text-curtron-sub">PDF File</label>
              <label className="mt-1 flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-curtron-line bg-curtron-bg text-sm text-curtron-sub cursor-pointer hover:border-curtron-green transition-colors">
                <Upload className="w-4 h-4" /> {pdf ? "PDF selected ✓" : "Choose a PDF…"}
                <input type="file" accept="application/pdf" onChange={onFile} className="hidden" data-testid="schedule-file" />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn onClick={upload} disabled={saving || !pdf} data-testid="schedule-upload-confirm">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Upload</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
